<script setup>
import { nextTick, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Buscar...'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  autofocus: {
    type: Boolean,
    default: false
  },
  debounceMs: {
    type: Number,
    default: 800
  },
  minLength: {
    type: Number,
    default: 2
  },
  showFilterDropdown: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue', 'search', 'clear', 'result-click'])

const { t } = useI18n()
const { searchWithTypes, results, loading, getAvailableTypes, getTypeConfig } = useSearch()
const { getImageUrl } = useImageUrl()

const inputRef = ref(null)
const isFocused = ref(false)
const showDropdown = ref(false)
const dropdownContentRef = ref(null)
const showTypeFilter = ref(false)
const typeFilterRef = ref(null)

// Tipos seleccionados en el filtro. Ref sin watchers: la búsqueda solo se
// relanza explícitamente desde toggleType/selectAllTypes, nunca por reactividad.
const selectedTypes = ref([])

// Todas las imágenes cargadas
const imageUrls = ref(new Map())

// W19: la clave de caché incluye el tipo — antes era solo `id` y un usuario
// con el mismo id que un fichero compartían entrada (imagen equivocada)
const imageCacheKey = (result) => `${result.type}:${result.id}`

// Tipos disponibles para filtrar
const availableTypes = computed(() => getAvailableTypes())

// Configuración de tipos para el filtro
const typeFilterOptions = computed(() => {
  return availableTypes.value.map(type => ({
    value: type,
    ...getTypeConfig(type)
  }))
})

// Etiqueta del filtro activo
const activeFilterLabel = computed(() => {
  if (selectedTypes.value.length === 0) {
    return t('search.filters.all')
  }
  if (selectedTypes.value.length === 1) {
    const type = selectedTypes.value[0]
    const config = getTypeConfig(type)
    return config ? t(config.labelKey) : type
  }
  return t('search.filters.multiple', { count: selectedTypes.value.length })
})

// Manejar click fuera del dropdown
const handleClickOutside = (event) => {
  if (showDropdown.value && dropdownContentRef.value) {
    if (!dropdownContentRef.value.contains(event.target) && !inputRef.value?.contains(event.target)) {
      showDropdown.value = false
    }
  }
  if (showTypeFilter.value && typeFilterRef.value) {
    if (!typeFilterRef.value.contains(event.target)) {
      showTypeFilter.value = false
    }
  }
}

// Cargar imágenes de los resultados
const loadResultImages = async () => {
  for (const result of results.value) {
    if (result.image && !imageUrls.value.has(imageCacheKey(result))) {
      try {
        const url = await getImageUrl(result.image, result.imageType, { usePlaceholder: false })
        if (url) {
          imageUrls.value.set(imageCacheKey(result), url)
        }
      } catch {
        // Silenciar errores de carga de imagen
      }
    }
  }
}

// Watch para cargar imágenes cuando cambien los resultados
watch(() => results.value, () => {
  loadResultImages()
}, { deep: true })

// Debounce para búsqueda
let debounceTimer = null
const onInput = (value) => {
  emit('update:modelValue', value)

  clearTimeout(debounceTimer)

  if (!value || value.length < props.minLength) {
    debounceTimer = null
    showDropdown.value = false
    return
  }

  debounceTimer = setTimeout(async () => {
    await performSearch()
  }, props.debounceMs)
}

// Manejar tecla Enter
const onKeydown = (e) => {
  if (e.key === 'Enter') {
    e.preventDefault()
    // Cancelar debounce pendiente al presionar Enter
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    if (props.modelValue && props.modelValue.length >= props.minLength) {
      performSearch()
    }
  } else if (e.key === 'Escape') {
    showDropdown.value = false
    showTypeFilter.value = false
  }
}

// Realizar búsqueda y mostrar dropdown
const performSearch = async () => {
  // Cancelar cualquier debounce pendiente
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }

  const types = selectedTypes.value.length > 0 ? [...selectedTypes.value] : null

  await searchWithTypes(props.modelValue, types, { size: 10 })
  emit('search', props.modelValue)

  // Cerrar filtros y mostrar resultados
  showTypeFilter.value = false
  showDropdown.value = true
}

// Limpiar búsqueda
const onClear = () => {
  clearTimeout(debounceTimer)
  debounceTimer = null
  emit('update:modelValue', '')
  emit('clear')
  showDropdown.value = false
  showTypeFilter.value = false
  imageUrls.value.clear()
  nextTick(() => {
    inputRef.value?.focus()
  })
}

// Toggle filtro por tipo
const toggleTypeFilter = () => {
  showTypeFilter.value = !showTypeFilter.value
  // Cerrar resultados cuando se abren los filtros
  if (showTypeFilter.value) {
    showDropdown.value = false
  }
}

// Seleccionar/deseleccionar tipo
const toggleType = (type) => {
  const index = selectedTypes.value.indexOf(type)
  if (index === -1) {
    selectedTypes.value.push(type)
  } else {
    selectedTypes.value.splice(index, 1)
  }

  // Si hay texto de búsqueda, reejecutar con debounce
  if (props.modelValue && props.modelValue.length >= props.minLength) {
    // Cancelar debounce pendiente
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
    // Nuevo debounce para dar tiempo a seleccionar más filtros
    debounceTimer = setTimeout(() => {
      performSearch()
    }, props.debounceMs)
  }
}

// Seleccionar todos los tipos
const selectAllTypes = () => {
  selectedTypes.value = []

  // Si hay texto de búsqueda, reejecutar con debounce
  if (props.modelValue && props.modelValue.length >= props.minLength) {
    // Cancelar debounce pendiente
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
    // Nuevo debounce para dar tiempo
    debounceTimer = setTimeout(() => {
      performSearch()
    }, props.debounceMs)
  }
}

// Verificar si un tipo está seleccionado
const isTypeSelected = (type) => selectedTypes.value.includes(type)

const handleFocus = () => {
  isFocused.value = true
}

const handleBlur = () => {
  isFocused.value = false
  // Cerrar dropdown después de un delay para permitir clicks en resultados
  setTimeout(() => {
    if (!showTypeFilter.value) {
      showDropdown.value = false
    }
  }, 200)
}

// Click en un resultado
const onResultClick = (result) => {
  showDropdown.value = false
  showTypeFilter.value = false
  emit('result-click', result)
  navigateTo(result.url)
}

// Exponer métodos para el componente padre
defineExpose({
  focus: () => inputRef.value?.focus?.(),
  blur: () => inputRef.value?.blur?.(),
  clear: onClear
})

// Lifecycle hooks para click outside
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  clearTimeout(debounceTimer)
})
</script>

<template>
  <div class="relative">
    <div class="flex w-full min-w-0 flex-1 items-stretch rounded-lg h-10 bg-slate-100 hover:bg-slate-100 focus-within:bg-slate-100 focus-within:ring-2 ring-primary/50 transition-all">
      <button
        v-if="showFilterDropdown"
        type="button"
        class="flex items-center gap-1 px-2 pl-3 text-slate-500 hover:text-slate-700 transition-colors border-r border-slate-200"
        :class="{ 'text-primary': selectedTypes.length > 0 }"
        @click.stop="toggleTypeFilter"
      >
        <Icon name="mdi:filter-variant" class="text-lg" />
        <Icon name="mdi:chevron-down" class="text-sm" />
      </button>

      <div v-if="selectedTypes.length > 0" class="flex items-center gap-1 pl-2">
        <button
          v-for="type in selectedTypes"
          :key="type"
          type="button"
          class="flex size-6 shrink-0 items-center justify-center rounded transition-opacity hover:opacity-70"
          :class="getTypeConfig(type)?.bgColor"
          :title="`${t(getTypeConfig(type)?.labelKey)} — ${t('common.remove')}`"
          @click.stop="toggleType(type)"
        >
          <Icon :name="getTypeConfig(type)?.icon" class="text-sm" :class="getTypeConfig(type)?.color" />
        </button>
      </div>
      <div
        v-if="$slots.icon"
        class="text-slate-400 flex items-center justify-center pl-3 min-w-fit"
      >
        <slot name="icon">
          <Icon name="material-symbols:search" class="text-[20px]" />
        </slot>
      </div>

      <input
        ref="inputRef"
        :value="modelValue"
        type="search"
        :placeholder="placeholder"
        :disabled="disabled"
        :autofocus="autofocus"
        autocomplete="off"
        spellcheck="false"
        class="flex-1 min-w-0 w-full rounded-lg border-none bg-transparent px-3 py-2 text-sm font-normal leading-normal text-slate-900 outline-none shadow-none placeholder:text-slate-400 focus:border-none focus:bg-transparent focus:outline-none focus:ring-0 hover:bg-transparent active:bg-transparent disabled:cursor-not-allowed disabled:opacity-50 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
        @input="onInput($event.target.value)"
        @keydown="onKeydown"
        @focus="handleFocus"
        @blur="handleBlur"
      />

      <div
        v-if="loading"
        class="absolute right-10 top-1/2 -translate-y-1/2"
      >
        <Icon name="mdi:loading" class="text-slate-400 text-lg animate-spin" />
      </div>
      <button
        v-if="modelValue && isFocused && !loading"
        type="button"
        class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
        aria-label="Limpiar búsqueda"
        @mousedown.prevent
        @click="onClear"
      >
        <Icon name="mdi:close-circle" class="text-[16px]" />
      </button>
    </div>
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 -translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 -translate-y-2"
    >
      <div
        v-if="showTypeFilter"
        ref="typeFilterRef"
        class="absolute top-full left-0 mt-2 z-50 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden min-w-[200px]"
      >
        <div class="px-3 py-2 border-b border-slate-100 bg-slate-50">
          <p class="text-xs font-medium text-slate-600">{{ t('search.filterByType') }}</p>
        </div>
        <div class="py-1">
          <button
            type="button"
            class="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
            @click="selectAllTypes"
          >
            <div
              class="w-6 h-6 rounded-md flex items-center justify-center transition-all shadow-sm"
              :class="selectedTypes.length === 0
                ? 'bg-slate-700 border-2 border-slate-700'
                : 'border-2 border-slate-400 bg-slate-50'"
            >
              <Icon
                v-if="selectedTypes.length === 0"
                name="mdi:check-bold"
                class="text-white text-base font-bold"
              />
              <Icon
                v-else
                name="mdi:minus"
                class="text-slate-400 text-sm"
              />
            </div>
            <span class="text-sm font-medium" :class="selectedTypes.length === 0 ? 'text-slate-900' : 'text-slate-700'">
              {{ t('search.filters.all') }}
            </span>
          </button>

          <div class="my-1 border-t border-slate-100" />

          <button
            v-for="option in typeFilterOptions"
            :key="option.value"
            type="button"
            class="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
            @click="toggleType(option.value)"
          >
            <div
              class="w-6 h-6 rounded-md flex items-center justify-center transition-all shadow-sm"
              :class="isTypeSelected(option.value)
                ? 'bg-slate-700 border-2 border-slate-700'
                : 'border-2 border-slate-400 bg-slate-50'"
            >
              <Icon
                v-if="isTypeSelected(option.value)"
                name="mdi:check-bold"
                class="text-white text-base font-bold"
              />
            </div>
            <div
              class="w-6 h-6 rounded flex items-center justify-center"
              :class="option.bgColor"
            >
              <Icon :name="option.icon" class="text-sm" :class="option.color" />
            </div>
            <span class="text-sm text-slate-700">{{ t(option.labelKey) }}</span>
          </button>
        </div>
      </div>
    </Transition>

    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 -translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 -translate-y-2"
    >
      <div
        v-if="showDropdown"
        ref="dropdownContentRef"
        class="absolute top-full left-0 right-0 mt-2 z-50 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden"
      >
        <div v-if="results.length > 0">
          <div class="px-4 py-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <p class="text-xs text-slate-500">
              {{ results.length }} {{ results.length === 1 ? t('search.result') : t('search.results') }}
            </p>
            <span
              v-if="selectedTypes.length > 0"
              class="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium"
            >
              {{ activeFilterLabel }}
            </span>
          </div>

          <div class="py-1 max-h-[400px] overflow-y-auto">
            <button
              v-for="result in results"
              :key="imageCacheKey(result)"
              type="button"
              @click.stop="onResultClick(result)"
              class="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left group"
            >
              <div
                class="flex-shrink-0 size-10 rounded-full flex items-center justify-center overflow-hidden"
                :class="result.meta.bgColor"
              >
                <img
                  v-if="imageUrls.get(imageCacheKey(result))"
                  :src="imageUrls.get(imageCacheKey(result))"
                  :alt="result.title"
                  class="w-full h-full object-cover"
                />
                <Icon
                  v-else
                  :name="result.meta.icon"
                  class="text-lg"
                  :class="result.meta.color"
                />
              </div>

              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <p class="text-sm font-medium text-slate-800 truncate group-hover:text-primary transition-colors">
                    {{ result.title }}
                  </p>
                  <span
                    class="text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0"
                    :class="[result.meta.bgColor, result.meta.color]"
                  >
                    {{ t(result.meta.typeLabel) }}
                  </span>
                </div>
                <p class="text-xs text-slate-500 truncate">
                  {{ result.subtitle }}
                </p>
              </div>

              <Icon
                name="mdi:chevron-right"
                class="text-slate-400 group-hover:text-primary transition-colors flex-shrink-0"
              />
            </button>
          </div>

          <div class="px-4 py-2 border-t border-slate-100 bg-slate-50">
            <p class="text-xs text-slate-400">
              {{ t('search.maxResults') }}
            </p>
          </div>
        </div>

        <div v-else-if="!loading" class="p-6">
          <div class="flex flex-col items-center text-center">
            <Icon name="mdi:search-web" class="text-4xl text-slate-300 mb-3" />
            <p class="text-sm text-slate-600">{{ t('search.noResults') }}</p>
            <p class="text-xs text-slate-400 mt-1">
              {{ t('search.tryDifferent') }}
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
